/**
 * Parses tool name from VishCoder tool_usage lines, e.g.
 * `[internal] Write\nArgs:`, `[internal] Read Args:`, `[external] ToolSearch\nArgs:`
 */
export function inferToolUsageKindFromContent(content: string): string {
    const head = content.slice(0, 512);
    const m = head.match(/\[(?:internal|external)\]\s+(\w+)/);
    if (m?.[1]) return m[1];
    return 'Other';
}

/** Preferred order for tool names in summary strings */
const TOOL_KIND_ORDER: string[] = [
    'Read',
    'Write',
    'Edit',
    'Bash',
    'Glob',
    'Grep',
    'ToolSearch',
    'StructuredOutput',
    'NotebookEdit',
    'Task',
    'WebFetch',
    'Other'
];

export function sortToolKindKeys(keys: string[]): string[] {
    return [...keys].sort((a, b) => {
        const ia = TOOL_KIND_ORDER.indexOf(a);
        const ib = TOOL_KIND_ORDER.indexOf(b);
        if (ia !== -1 && ib !== -1) return ia - ib;
        if (ia !== -1) return -1;
        if (ib !== -1) return 1;
        return a.localeCompare(b);
    });
}

export interface AiGroupMessageLike {
    category?: string;
    content: string;
    toolUsageKind?: string;
}

/**
 * Summary line for the AI Activity header (counts + tool breakdown).
 */
export function summariseAiGroup(messages: AiGroupMessageLike[]): string {
    let thinking = 0;
    let text = 0;
    const toolByKind: Record<string, number> = {};

    for (const m of messages) {
        if (m.category === 'ai_thinking') thinking++;
        else if (m.category === 'ai_text') text++;
        else if (m.category === 'ai_tool_usage') {
            const kind = m.toolUsageKind || inferToolUsageKindFromContent(m.content);
            toolByKind[kind] = (toolByKind[kind] || 0) + 1;
        }
    }

    const parts: string[] = [];
    for (const k of sortToolKindKeys(Object.keys(toolByKind))) {
        const n = toolByKind[k];
        parts.push(`${n} ${k}`);
    }
    if (thinking > 0) parts.push(`${thinking} thinking`);
    if (text > 0) parts.push(`${text} text`);

    const n = messages.length;
    const label = `${n} message${n !== 1 ? 's' : ''}`;
    return parts.length > 0 ? `${label}: ${parts.join(', ')}` : label;
}

/** Matches `trackBuildStatus` build detail field — cumulative counts independent of stored rows. */
export interface AiActivitySummaryInput {
    thinking: number;
    aiText: number;
    toolByKind: Record<string, number>;
}

export function totalAiFromSummary(s: AiActivitySummaryInput): number {
    const totalTools = Object.values(s.toolByKind).reduce((a, b) => a + b, 0);
    return s.thinking + s.aiText + totalTools;
}

/**
 * Same header format as {@link summariseAiGroup}, using persisted cumulative counts.
 */
export function formatAiActivitySummaryLabel(s: AiActivitySummaryInput | null | undefined): string | null {
    if (!s) return null;
    const totalTools = Object.values(s.toolByKind).reduce((a, b) => a + b, 0);
    const total = s.thinking + s.aiText + totalTools;
    if (total === 0) return null;

    const parts: string[] = [];
    for (const k of sortToolKindKeys(Object.keys(s.toolByKind))) {
        const n = s.toolByKind[k];
        if (n > 0) parts.push(`${n} ${k}`);
    }
    if (s.thinking > 0) parts.push(`${s.thinking} thinking`);
    if (s.aiText > 0) parts.push(`${s.aiText} text`);

    return parts.length > 0 ? `${total} messages: ${parts.join(', ')}` : `${total} messages`;
}
